/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { consume } from '@lit/context';
import { t } from 'i18next';
import { html, LitElement, PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { getShowVisualDesignerToken } from '@/api-sdk';
import {
  ConfigurationContextType,
  ConfiguratorContextType,
  configuratorGlobal,
  global,
  GlobalComponentType,
  GlobalConfigurationContext,
  personalisation,
  PersonalisationType,
  visualDesigner,
  VisualDesignerContextType,
} from '@/context';
import { VisualDesignerDesignSettings } from '@/types';

import '@/components/messages/fg-message';

@customElement('fg-visual-designer')
export class FgVisualDesigner extends LitElement {
  @property({ type: String })
  token = '';

  @property({ type: String })
  designId = '';

  @property({ type: String })
  printOptionCode = '';

  @property({ type: Number })
  quantity = 1;

  @property({ type: String })
  unitPriceExcludingVat = '0';

  @state()
  designSettings?: VisualDesignerDesignSettings[];

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: personalisation.context, subscribe: true })
  personalisationContext: PersonalisationType[] = [];

  @consume({ context: visualDesigner.context, subscribe: true })
  visualDesignerContext!: VisualDesignerContextType;

  @consume({ context: GlobalConfigurationContext, subscribe: true })
  configurationContext!: ConfigurationContextType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  private zakekeCustomizer?: any;
  private zakekeIframe?: HTMLElement;

  // Light DOM for this component, so the iframe is not in a shadow DOM
  createRenderRoot() {
    return this;
  }

  protected willUpdate(_changedProperties: PropertyValueMap<any>) {
    if (
      _changedProperties.has('designId') ||
      _changedProperties.has('printOptionCode') ||
      _changedProperties.has('quantity') ||
      _changedProperties.has('unitPriceExcludingVat')
    ) {
      this._removeIframe();
      if (this.token) {
        this.createVisualDesigner(this.token);
      }
    }
  }

  private createTemporaryGlobalContainer() {
    // Cleanup temporary container
    const existingContainer = document.getElementById('zakeke-container');
    if (existingContainer) {
      existingContainer.remove();
    }

    // Create temporary container in global DOM
    const tempContainer = Object.assign(document.createElement('div'), {
      id: 'zakeke-container',
      style: 'position:fixed;top:-9999px;left:-9999px;',
    });
    document.body.appendChild(tempContainer);
  }

  closeVisualDesigner = () => {
    this._removeIframe();
    this.dispatchEvent(
      new CustomEvent('visual-designer-close', {
        composed: true,
      }),
    );
  };

  closeAndSaveVisualDesigner(designId: string) {
    this._removeIframe();
    if (!designId) {
      console.error('No design id found');
      return;
    }

    this.dispatchEvent(
      new CustomEvent('visual-designer-save', {
        composed: true,
        detail: {
          visualDesignId: designId,
          designSettings: this.designSettings,
        },
      }),
    );
  }

  async createVisualDesigner(visualDesignerToken: string) {
    try {
      if (!visualDesignerToken)
        throw new Error('Visual designer token not found');

      if (this.zakekeCustomizer) {
        this._removeIframe();
      }

      const { locale } = this.globalContext;
      const { productId, productName } = this.visualDesignerContext;

      if (!productId || !productName || !this.token) return null;

      this.createTemporaryGlobalContainer();

      // ZakekeDesigner is loaded from an external script and available globally
      this.zakekeCustomizer = new (window as any).ZakekeDesigner();

      if (!this.zakekeCustomizer)
        throw new Error('Zakeke Customizer not found');

      const config = {
        tokenOauth: this.token,
        productId,
        productName,
        designId: this.designId,
        currency: 'EUR',
        culture: locale.replace('_', '-'),
        mobileVersion: false,
        labelTax: 'excluding',
        quantity: this.quantity,
        cartButtonText: t('actions.saveVisualDesign'),
        hideVariants: true,
        getProductInfo: this.getProductInfo,
        getProductPrice: this.getProductPrice,
        getProductAttribute: this.getProductAttribute,
        addToCart: this.handleAddToCart,
        editAddToCart: this.handleAddToCart,
        onBackClicked: this.closeVisualDesigner,
        isNamesAndNumbers: false,
        isSaveDesign: false,
        sides: this.getSides(),
        selectedAttributes: this.getSelectedAttributesCodes(),
        firstSideId: this.printOptionCode,
        firstSidePrintingMethods: this.getPrintingMethods(),
        unitPriceExcludingVat: this.unitPriceExcludingVat,
      };

      //Create Iframe in globale container
      this.zakekeCustomizer.createIframe(config);

      // Wait for iframe to load, then move to component
      await this.waitForIframeLoad();
      this.moveIframeToComponent();
    } catch (error) {
      const wrapper = this.querySelector('#zakeke-wrapper');
      if (wrapper) {
        wrapper.innerHTML = `<fg-message part="wholesale" type="error" message="${t('errors.visualDesignerLoadError')}"></fg-message>`;
      }
      console.error(
        'Something went wrong while loading the visual designer: ',
        error,
      );
    }
  }

  private waitForIframeLoad(): Promise<void> {
    return new Promise(resolve => {
      const container = document.querySelector('#zakeke-container');
      if (!container) {
        console.error('No container found');
        resolve();
        return;
      }

      const observer = new MutationObserver(() => {
        const iframe = container.querySelector<HTMLIFrameElement>('iframe');
        if (iframe) {
          this.zakekeIframe = iframe as HTMLElement;
          Object.assign(this.zakekeIframe.style, {
            width: '90dvw',
            height: '90dvh',
            border:
              '1px solid var(--fg-gray--300, var(--fg-fallback-gray--300))',
          });
          observer.disconnect();
          resolve();
        }
      });

      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    });
  }

  private _removeIframe() {
    const wrapper = this.querySelector('#zakeke-wrapper');

    if (this.zakekeCustomizer) this.zakekeCustomizer.removeIframe();

    if (wrapper) wrapper.innerHTML = '';
  }

  private moveIframeToComponent() {
    if (this.zakekeIframe) {
      const wrapper = this.querySelector('#zakeke-wrapper');
      if (wrapper) {
        // Remove existing iframe in wrapper first, just in case
        const hasIframe = wrapper.querySelector('iframe');
        if (hasIframe && hasIframe !== this.zakekeIframe) hasIframe.remove();
        wrapper.appendChild(this.zakekeIframe);
      }
    }
  }

  async connectedCallback() {
    super.connectedCallback();
    const visualDesignerToken = await getShowVisualDesignerToken();
    this.token = visualDesignerToken;
    this.createVisualDesigner(visualDesignerToken);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._removeIframe();
  }

  getProductInfo = (info: string) => {
    const infoJson = JSON.parse(info);

    this.designSettings = infoJson?.design?.sides
      ?.filter((side: any) => side.usedColors.length > 0)
      .map((side: any) => ({
        code: side.referenceKey,
        colorCount: side.usedColors.length,
        hasDesignElements: side.hasDesignElements,
      }));

    return {
      price: this.unitPriceExcludingVat || 0,
      isOutOfStock: false,
    };
  };

  getProductPrice() {
    return {
      price: this.unitPriceExcludingVat || 0,
      isOutOfStock: false,
    };
  }

  getProductAttribute() {
    return { attributes: [] };
  }

  getSelectedAttributesCodes() {
    if (!this.visualDesignerContext.variant) {
      return {}; // empty object is valid for Zakeke
    }

    const selectedAttributes = Object.fromEntries(
      Object.entries(this.visualDesignerContext.variant).map(
        ([key, { code }]) => [key, code],
      ),
    );

    return selectedAttributes;
  }

  getSides() {
    return (
      this.configurationContext?.configurationData?.printOptions?.map(
        item => item.code,
      ) ||
      this.visualDesignerContext.selectedPrintOptions?.map(item => item.code) ||
      []
    );
  }

  getPrintingMethods(): Record<string, string> {
    const { printOptions } = this.visualDesignerContext;
    if (!printOptions?.length) {
      return {};
    }
    return Object.fromEntries(
      printOptions.flatMap(printOption => {
        const { code, printingMethod } = printOption;
        return printingMethod != null ? [[code, printingMethod]] : [];
      }),
    );
  }

  updateColorCountEvent = () => {
    visualDesigner.providerUpdateEvent(this, {
      ...this.visualDesignerContext,
      selectedPrintOptions: this.designSettings,
    });

    this.dispatchEvent(
      new CustomEvent('update-visual-designer-color-count', {
        detail: this.designSettings,
        composed: true,
      }),
    );
  };

  handleAddToCart = async (zakekeData: any) => {
    this.closeAndSaveVisualDesigner(zakekeData.designid);

    if (this.designSettings && this.designSettings.length > 0) {
      this.updateColorCountEvent();
    }
  };

  render() {
    return html`
      <div id="zakeke-wrapper"></div>
    `;
  }
}
